import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const initialCheckDone = useRef(false);

  const checkAuth = useCallback(async (currentToken) => {
    // If returning from Google OAuth callback with token=... in the fragment,
    // skip the /me check. AuthCallback will consume the token first.
    if (window.location.hash?.includes('token=')) {
      setLoading(false);
      return;
    }

    // If no token and no cookie potential, skip the check
    const tokenToUse = currentToken || token;
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (tokenToUse) {
        headers['Authorization'] = `Bearer ${tokenToUse}`;
      }

      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Only clear token if we had one and it was invalid
        if (tokenToUse) {
          localStorage.removeItem('token');
          setToken(null);
        }
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Run auth check only once on mount
  useEffect(() => {
    if (!initialCheckDone.current) {
      initialCheckDone.current = true;
      const storedToken = localStorage.getItem('token');
      checkAuth(storedToken);
    }
  }, []);  // Empty dependency array - run only on mount

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();

    // If the user has MFA enabled, the backend returns a challenge instead
    // of a JWT. Surface that to the caller so it can show the code prompt.
    if (data.mfa_required) {
      return { mfa_required: true, challenge: data.challenge, email: data.email };
    }

    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const completeMfa = async (challenge, code) => {
    const response = await fetch(`${API}/auth/mfa/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ challenge, code }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'MFA verification failed');
    }
    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, firmName, selectedPlan, extras = {}) => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        name,
        email,
        password,
        firm_name: firmName,
        selected_plan: selectedPlan || undefined,
        billing_cycle: extras.billing_cycle,
        seats: extras.seats,
        referral_code: extras.referral_code || undefined,
        privacy_accepted: !!extras.privacy_accepted,
        privacy_version: extras.privacy_version,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const loginWithGoogle = async () => {
    // Direct Google OAuth2 - backend returns the Google consent URL.
    // No third-party proxy. Cookie-based CSRF protection (oauth_state).
    const response = await fetch(`${API}/auth/google/start`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Could not start Google sign-in');
    }
    const { url } = await response.json();
    window.location.href = url;
  };

  const consumeOAuthToken = async (token) => {
    // Called by AuthCallback after backend redirects with #token=... in the URL fragment.
    localStorage.setItem('token', token);
    setToken(token);
    await checkAuth(token);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const createTenant = async (name, subscriptionPlan = 'starter') => {
    const response = await fetch(`${API}/tenants`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ name, subscription_plan: subscriptionPlan })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create organization');
    }

    const tenant = await response.json();
    // Refresh user data
    await checkAuth();
    return tenant;
  };

  const getAuthHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }, [token]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      consumeOAuthToken,
      completeMfa,
      logout,
      createTenant,
      getAuthHeaders,
      setUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
